import React, { useEffect, useState } from 'react';
import { useMount } from 'react-use';
import { useLocation } from 'react-router-dom';
import {
  Button,
  Switch,
  DatePicker,
  Select,
  Spin,
  Table,
  Popover,
  notification,
  Avatar,
  Tooltip
} from 'antd';
import { Comment } from '@ant-design/compatible';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { PageHeader } from '@ant-design/pro-layout';
import moment from 'moment';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useRecoilState, useRecoilValue, useRecoilValueLoadable } from 'recoil';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import api from '../../../../api/api';
import { configSelector, businessesSelector } from '../../../../atoms/Selectors';
import { percent, randomColor, renderShortDate, thousand, toCurrency } from '../../../utils/functions';
import { profileAtom } from '../../../../atoms/Atoms';
import Grocefy from '../../../../assets/images/grocefyLogoAlone.png';
import envConfig from '../../../../envConfig';

function LossSalesMonthly() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const businessId = queryParams.get('businessId');
  const lossType = queryParams.get('lossType');
  const showTotals = queryParams.get('showTotals');
  const timer = queryParams.get('timer');
  const config = useRecoilValueLoadable(configSelector);
  const { t } = useTranslation();
  const { RangePicker } = DatePicker;
  const { Option } = Select;
  const profile = useRecoilValue(profileAtom);
  const [loading, setLoading] = useState(false);
  const [dataPoints, setDataPoints] = useState([]);
  const [flatDataPoints, setFlatDataPoints] = useState([]);
  const [labels, setLabels] = useState([]);
  const [selectedYear, setSelectedYear] = useState(dayjs().startOf('year'));
  const [business, setBusiness] = useState(null);
  const [selectedBusinessId, setSelectedBusinessId] = useState(businessId ?? '');
  const [loadingBusiness, setLoadingBusiness] = useState(false);
  const businesses = useRecoilValueLoadable(businessesSelector);
  const [selectedLossType, setSelectedLossType] = useState(lossType ?? 'cost');
  const [hideTotals, setHideTotals] = useState(showTotals !== 'true');
  const [refreshTimer, setRefreshTimer] = useState(timer ? parseInt(timer, 10) : 0);
  const [isInitialized, setIsInitialized] = useState(false);

  function showMessage(message) {
    notification.open({
      message: '',
      description: (
        <Comment
          author={<span>Grocefy</span>}
          avatar={<Avatar src={Grocefy} alt="grocefy" />}
          content={
            <p className="text-sm">
              {message}
            </p>
          }
          datetime={
            <Tooltip title={moment().format('YYYY-MM-DD HH:mm:ss')}>
              <span>{moment().fromNow()}</span>
            </Tooltip>
          }
        />
      ),
    });
  }

  function getData() {
    setLoading(true);
    const startDate = selectedYear;
    let endDate = startDate.endOf('year').startOf('month');
    if (selectedYear.isSame(dayjs().startOf('year'))) {
      endDate = dayjs().startOf('month');
    }
    api.get(`reports/lossvssales/monthly?startDate=${startDate.format('YYYY-MM-DDTHH:mm:ssZ')}&endDate=${endDate.format('YYYY-MM-DDTHH:mm:ssZ')}&businessId=${selectedBusinessId}`)
      .then((response) => {
        setLoading(false);
        const interval = window.localStorage.getItem(`${envConfig.REACT_APP_PREFIX}-loss-report-timer`);
        if (interval > 0) {
          timerLogic();
        }
        if (response.data.success) {
          setLabels(response.data.data);
          let allitems = _.flatMap(response.data.data, 'data');
          allitems = _.groupBy(allitems, 'storeName');
          const mapped = Object.keys(allitems).map((key) => ({ storeName: key, color: randomColor(), data: allitems[key] }));
          setDataPoints(mapped);
        }
      })
      .catch((error) => {
        console.error(error);
        setLoading(false);
      });
  }

  function getBusiness() {
    setLoadingBusiness(true);
    api.get(`businesses/${businessId}`)
      .then((response) => {
        setLoadingBusiness(false);
        if (response.data.success) {
          setBusiness(response.data.data);
        }
      })
      .catch((error) => {
        setLoadingBusiness(false);
      });
  }

  function getCurrentUrl() {
    let url = `${window.location.origin}/reports/loss/targetvsactual?`;
    const params = [];
    if (selectedBusinessId?.length > 0) {
      params.push(`businessId=${selectedBusinessId}`);
    }
    params.push(`showTotals=${!hideTotals}`);
    params.push(`lossType=${selectedLossType}`);
    params.push(`timer=${refreshTimer}`);
    url += params.join('&');
    navigator.clipboard.writeText(url);
    showMessage(t('link_copied_to_clipboard'));
  }

  function timerLogic() {
    const interval = window.localStorage.getItem(`${envConfig.REACT_APP_PREFIX}-loss-report-timer`);
    const lastTimeoutId = window.localStorage.getItem(`${envConfig.REACT_APP_PREFIX}-loss-report-timerId`);
    if (lastTimeoutId) {
      window.clearInterval(lastTimeoutId);
    }

    if (interval > 0) {
      const tempId = setTimeout(() => {
        if (!loading) {
          if (!loading) {
            getData();
          }
          timerLogic();
        }
      }, interval * 60000);
      window.localStorage.setItem(`${envConfig.REACT_APP_PREFIX}-loss-report-timerId`, tempId);
    }
  }

  useEffect(() => {
    getData();
    if (businessId) {
      getBusiness();
    }
    setTimeout(() => {
      setIsInitialized(true);
    }, 1000);
    window.localStorage.setItem(`${envConfig.REACT_APP_PREFIX}-loss-report-timer`, timer ? parseInt(timer, 10) : 0);
  }, []);

  useEffect(() => {
    if (isInitialized) {
      window.localStorage.setItem(`${envConfig.REACT_APP_PREFIX}-loss-report-timer`, refreshTimer);
      setTimeout(() => timerLogic(), 1000);
    }
  }, [refreshTimer]);

  useEffect(() => {
    getData();
    if (isInitialized) {
      timerLogic();
    }
  }, [selectedYear]);

  useEffect(() => {
    // const temp = _.map(dataPoints, (d) => ({
    //   storeName: d.storeName,
    //   loss: _.sumBy(d.data, 'loss'),
    //   lossVsCost: _.sumBy(d.data, 'lossVsCost'),
    //   lossVsSales: _.sumBy(d.data, 'lossVsSales'),
    //   salesCost: _.sumBy(d.data, 'salesCost'),
    //   salesPrice: _.sumBy(d.data, 'salesPrice'),
    //   zeroCostItems: _.sumBy(d.data, 'zeroCostItems')
    // }));
    const temp = _.flatMap(dataPoints, 'data');
    setFlatDataPoints(temp);
  }, [dataPoints]);

  return (
    <div>
      <PageHeader
        className="mb-4 px-0"
        title={(
          <div className="space-x-2">
            <span>{t('loss')}: {t('monthly')}</span>
            <Popover content={(
              <div>
                <p className="font-bold">{t('legend')}</p>
                <div>{t('loss')} = ({t('movement_amount')} * {t('item_cost')})</div>
                <div>{t('sales_cost')} = ({t('item_amount_sold')} * {t('item_cost')})</div>
                <div>{t('sales_price')} = ({t('item_amount_sold')} * {t('item_price')})</div>
                <div className="flex">
                  <div className="my-auto mr-1">{t('loss_vs_cost')} = </div>
                  <div className="frac">
                    <span>{t('loss')}</span>
                    <span className="symbol">/</span>
                    <span className="bottom">{t('sales_cost')}</span>
                  </div>
                </div>
                <div className="flex">
                  <div className="my-auto mr-1">{t('loss_vs_price')} = </div>
                  <div className="frac">
                    <span>{t('loss')}</span>
                    <span className="symbol">/</span>
                    <span className="bottom">{t('sales_price')}</span>
                  </div>
                </div>
              </div>
            )}>
              <FontAwesomeIcon icon={faInfoCircle} />
            </Popover>
          </div>
        )}
        extra={[
          <div className="space-x-1">
            <strong>{t('show_totals')}</strong>
            <Switch
              checked={!hideTotals}
              onChange={(obj) => setHideTotals(!obj)}
              disabled={loading || !profile}
            />
          </div>,
          <Select
            value={selectedBusinessId}
            style={{ width: 200 }}
            disabled={businesses?.state === 'loading' || businesses.state === 'hasError' || loadingBusiness}
            loading={businesses?.state === 'loading' || loadingBusiness}
            onChange={(obj) => setSelectedBusinessId(obj)}
          >
            <Option value="">{t('global')}</Option>
            {businesses.state === 'hasValue' && (
              <>
                {_.map(_.sortBy(businesses.contents.data.data.results, (s) => s.name, 'asc'), (b) => (
                  <Option
                    value={b.id}
                    key={b.id}
                  >
                    {b.name}
                  </Option>
                ))}
              </>
            )}
            {business && (
              <Option value={business?.id}>{business?.name}</Option>
            )}
          </Select>,
          <Select
            value={selectedLossType}
            style={{ width: 100 }}
            loading={loading}
            onChange={(obj) => setSelectedLossType(obj)}
          >
            <Option value="cost">{t('cost')}</Option>
            <Option value="price">{t('price')}</Option>
          </Select>,
          <DatePicker
            picker="year"
            value={dayjs(selectedYear)}
            onChange={(date, dateString) => {
              setSelectedYear(dayjs(dateString));
            }}
          />,
          <Select
            value={refreshTimer}
            style={{ width: 200 }}
            loading={loading}
            onChange={(obj) => setRefreshTimer(obj)}
          >
            <Option value={0}>{t('no_refresh')}</Option>
            <Option value={5}>{t('5_min_refresh')}</Option>
            <Option value={10}>{t('10_min_refresh')}</Option>
            <Option value={15}>{t('15_min_refresh')}</Option>
            <Option value={30}>{t('30_min_refresh')}</Option>
            <Option value={60}>{t('60_min_refresh')}</Option>
          </Select>,
          <Button
            size="small"
            type="primary"
            onClick={() => getData()}
          >
            {t('reload')}
          </Button>
        ]}
      />
      {profile && (
        <div className="flex">
          <Button
            size="small"
            type="primary"
            className="ml-auto"
            onClick={() => getCurrentUrl()}
          >
            {t('get_url_for_report')}
          </Button>
        </div>
      )}
      <Spin spinning={loading}>
        <div className="grid grid-cols-1">
          <div className="col-span-1" style={{ height: '74vh' }}>
            <Line
              data={{
                labels: _.map(labels, (s) => dayjs(s.month).format('MMM')),
                datasets: _.map(dataPoints, (s, i) => ({
                  label: s.storeName,
                  backgroundColor: s.color,
                  borderColor: `${s.color}cc`,
                  data: _.map(s.data, (x) => (selectedLossType === 'cost' ? x.lossVsCost : x.lossVsSales)),
                })),
              }}
              options={{
                maintainAspectRatio: false,
                scales: {
                  y: {
                    ticks: {
                      beginAtZero: true,
                      callback: (value, index, ticks) => (!hideTotals ? `${percent(value)}%` : ''),
                      font: {
                        size: 20
                      }
                    }
                  },
                  x: {
                    ticks: {
                      font: {
                        size: 20
                      }
                    }
                  }
                },
                responsive: true,
                plugins: {
                  zoom: {
                    pan: {
                      enabled: true,
                      mode: 'x',
                    },
                    zoom: {
                      wheel: {
                        enabled: true,
                      },
                      pinch: {
                        enabled: true
                      },
                      mode: 'x',
                    }
                  },
                  legend: {
                    position: 'top',
                  },
                  title: {
                    display: false,
                  },
                  datalabels: {
                    color: 'black',
                    // formatter: (value, context) => (!hideTotals ? value : ''),
                    formatter: (value, context) => '',
                    // labels: {
                    //   title: {
                    //     anchor: 'end',
                    //     align: 'top',
                    //     offset: -30,
                    //   },
                    // }
                  }
                },
              }}
            />
          </div>
          <div className={`col-span-1 ${hideTotals ? 'hidden' : ''}`}>
            <Table
              dataSource={flatDataPoints}
              bordered
              columns={[
                {
                  title: t('store'),
                  key: 'store',
                  align: 'center',
                  className: 'text-xs',
                  sorter: (a, b) => a.storeName.localeCompare(b.storeName),
                  render: (row) => <span>{row.storeName}</span>,
                },
                {
                  title: t('month'),
                  key: 'month',
                  align: 'center',
                  className: 'text-xs',
                  sorter: (a, b) => {
                    const aDay = dayjs(a);
                    const bDay = dayjs(b);
                    return aDay.isSame(bDay) || aDay.isBefore(bDay);
                  },
                  render: (row) => <span>{dayjs(row.month).format('MMM')}</span>,
                },
                {
                  title: t('loss'),
                  key: 'loss',
                  align: 'center',
                  className: 'text-xs',
                  sorter: (a, b) => a.loss <= b.loss,
                  render: (row) => <span>{toCurrency(row.loss)}</span>,
                },
                {
                  title: selectedLossType === 'cost' ? t('sales_cost') : t('sales_price'),
                  key: 'sales',
                  align: 'center',
                  className: 'text-xs',
                  sorter: (a, b) => (selectedLossType === 'cost' ? a.salesCost <= b.salesCost : a.salesPrice <= b.salesPrice),
                  render: (row) => <span>{toCurrency(selectedLossType === 'cost' ? row.salesCost : row.salesPrice)}</span>,
                },
                {
                  title: selectedLossType === 'cost' ? t('loss_vs_cost') : t('loss_vs_price'),
                  key: 'lossvs',
                  align: 'center',
                  className: 'text-xs',
                  sorter: (a, b) => (selectedLossType === 'cost' ? a.lossVsCost <= b.lossVsCost : a.lossVsSales <= b.lossVsSales),
                  render: (row) => <span>{percent(selectedLossType === 'cost' ? row.lossVsCost : row.lossVsSales)}%</span>,
                },
                {
                  title: t('zero_cost_items'),
                  key: 'zeroCostItems',
                  align: 'center',
                  className: `text-xs ${selectedLossType === 'cost' ? '' : 'hidden'}`,
                  sorter: (a, b) => a.zeroCostItems <= b.zeroCostItems,
                  render: (row) => <span>{thousand(row.zeroCostItems)}</span>,
                },
              ]}
            />
          </div>
        </div>
      </Spin>
    </div>
  );
}

export default LossSalesMonthly;
